<template>
  <layout>
    <template v-slot:title>Related Content</template>
    <div class="mb-1">
      <switch-field
        id="show-template-author-blocks"
        v-model="showTemplateAuthorBlocks"
        inline
        label="Show template author blocks"
      />
    </div>
    <filter-control label="Filter" v-model="filter" />
    <div v-if="loaded">
      <warning v-if="!sharedoId">
        This document is not linked to a Sharedo work item.
      </warning>
      <div v-else>
        <div
          v-if="tags.length > 0"
          class="
            tag-list
            d-flex
            flex-justify-center flex-align-center flex-wrap
          "
        >
          <tag
            v-for="t in tags"
            class="cursor-pointer"
            :key="t.tag"
            :tag="t.tag"
            :count="t.count"
            :selected="t.selected"
            @click="tagClicked(t)"
          ></tag>
        </div>
        <div v-if="relatedItems.length > 0">
          <related-content-item
            v-for="item in filteredItems"
            :key="item.templateSystemName"
            :related-item="item"
            :show-template-author-blocks="showTemplateAuthorBlocks"
            @insert-block="startInsertBlock"
            @block-refresh="startContentRefresh"
          />
        </div>
        <div v-else>
          <p><i>There is no related content for this work item.</i></p>
        </div>
      </div>
    </div>
    <template v-slot:modal>
      <content-block-modal
        v-if="showContentBlockModal"
        :content-block="currentBlock"
        :show-edit-tab="false"
        @insert="insertContentBlock"
        @add-tag="insertContentBlockAsTag"
        @close="modalClosed"
      />
      <business-rule-modal
        v-if="showRuleModal"
        title="Insert Content Block"
        @continue="ruleSelected"
        @close="ruleModalClosed"
      />
      <refresh-modal
        v-if="showRefreshModal"
        :content-block="currentBlock"
        @update-block="displayRefreshConfirmModal"
        @close="cancelRefreshContentBlocks"
      />
      <confirm-modal
        v-if="showRefreshConfirmModal"
        message="Do you want to update your document's content?"
        @confirm="refreshContentBlocks"
        @cancel="cancelRefreshContentBlocks"
      />
    </template>
  </layout>
</template>
<script>
import contentBlocksMixin from "@/mixins/contentBlocks";
import refreshContentMixin from "@/mixins/refreshContent";
import FilterControl from "@/components/common/filtering/FilterControl.vue";
import RelatedContentItem from "@/components/related-content/RelatedContentItem.vue";
import ContentBlockModal from "@/components/content-blocks/ContentBlockModal.vue";
import BusinessRuleModal from "@/components/business-rules/BusinessRuleModal.vue";
import RefreshModal from "@/components/content-blocks/RefreshModal.vue";
import ConfirmModal from "@/components/common/confirmations/ConfirmModal.vue";
import Tag from "@/components/common/tags/Tag.vue";
import customProperties from "@/services/word/customPropertiesService";
import relatedContent from "@/services/sharedo/relatedContentService";
import contentBlocks from "@/services/sharedo/contentBlocksService";
import { Warning } from "@/components/common/banners";

const loadRelatedContent = async function () {
  this.logDebug("Loading related content.");

  try {
    this.relatedItems = await relatedContent.getFor(this.sharedoId);
    this.logInfo("Loaded related content.", {
      relatedItems: this.relatedItems,
    });
  } catch (error) {
    this.logError("Unable to load related content.", { error });
    this.$store.commit("error", { message: "Unable to load related content." });
  }
};

const setCurrentBlock = async function (selectedBlock) {
  this.logDebug("Block selected.", { selectedBlock });

  try {
    const result = await contentBlocks.loadOne(
      selectedBlock.systemName,
      this.sharedoId
    );

    this.currentBlock = {
      ...result,
      title: selectedBlock.title,
      templateType: selectedBlock.type,
      versionNumber: selectedBlock.versionNumber,
      versionDate: selectedBlock.versionDate,
      availableForDocumentAuthors: selectedBlock.availableForDocumentAuthors,
      availableForTemplateAuthors: selectedBlock.availableForTemplateAuthors,
    };

    this.logInfo("Block loaded.", { block: this.currentBlock });
  } catch (error) {
    this.logError("Unable to load content block.", { error, selectedBlock });
    this.$store.commit("error", { message: "Unable to load content block." });
  }
};

const startContentRefresh = async function (selectedBlock) {
  await this.setCurrentBlock(selectedBlock);
  this.showRefreshModal = true;
};

const startInsertBlock = async function (selectedBlock) {
  await this.setCurrentBlock(selectedBlock);
  this.showContentBlockModal = true;
};

const tagClicked = async function (t) {
  if (t.selected) {
    const tag = this.selectedTags.find((st) => st === t.tag);
    this.selectedTags.splice(this.selectedTags.indexOf(tag), 1);
  } else {
    this.selectedTags.push(t.tag);
  }
};

export default {
  components: {
    FilterControl,
    Warning,
    RelatedContentItem,
    RefreshModal,
    ConfirmModal,
    Tag,
    ContentBlockModal,
    BusinessRuleModal,
  },
  mixins: [contentBlocksMixin, refreshContentMixin],
  data: function () {
    return {
      filter: "",
      sharedoId: "",
      relatedItems: [],
      loaded: false,
      selectedTags: [],
      showTemplateAuthorBlocks: false,
    };
  },
  computed: {
    tags: function () {
      const tags = [];

      this.relatedItems.forEach((item) => {
        item.blocks.forEach((block) => {
          if (
            block.availableForDocumentAuthors ||
            (this.showTemplateAuthorBlocks && block.availableForTemplateAuthors)
          ) {
            block.tags.forEach((tag) => {
              const existingTag = tags.find((t) => t.tag === tag);

              if (existingTag) {
                existingTag.count++;
              } else {
                tags.push({
                  tag,
                  count: 1,
                  selected: this.selectedTags.some((t) => t === tag),
                });
              }
            });
          }
        });
      });

      return tags;
    },
    filteredItems: function () {
      let results = [];

      if (this.selectedTags.length === 0) {
        results = this.relatedItems;
      } else {
        results = this.relatedItems.filter((item) =>
          item.blocks.some((block) =>
            block.tags.some(
              (tag) =>
                this.selectedTags.some((t) => t === tag) &&
                (block.availableForDocumentAuthors ||
                  (this.showTemplateAuthorBlocks &&
                    block.availableForTemplateAuthors))
            )
          )
        );
      }

      if (this.filter) {
        const lcFilter = this.filter.toLowerCase();

        results = results.filter(
          (item) =>
            item.templateTitle.toLowerCase().indexOf(lcFilter) > -1 ||
            item.blocks.some(
              (block) =>
                block.title.toLowerCase().indexOf(lcFilter) > -1 &&
                (block.availableForDocumentAuthors ||
                  (this.showTemplateAuthorBlocks &&
                    block.availableForTemplateAuthors))
            )
        );
      }

      return results;
    },
  },
  methods: {
    setCurrentBlock,
    loadRelatedContent,
    startInsertBlock,
    startContentRefresh,
    tagClicked,
  },
  mounted: async function () {
    this.sharedoId = await customProperties.get("SharedoActivityId");
    if (!!this.sharedoId) {
      this.loadRelatedContent();
    }
    this.loaded = true;
  },
};
</script>
<style scoped>
.tag-list {
  margin-bottom: 10px;
}
</style>
